// profile/hooks/useCachedFetch.js
import { useEffect, useRef, useState } from 'react';

// 超シンプルなメモリキャッシュ（ページリロードしたら消える）
const cache = new Map(); // key -> { data, ts }
const inflight = new Map(); // key -> Promise

export default function useCachedFetch(key, fetcher, { enabled = true, ttlMs = 60_000 } = {}) {
  const [data, setData] = useState(() => cache.get(key)?.data ?? null);
  const [loading, setLoading] = useState(enabled && !cache.has(key));
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const refetch = async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);

    // abort previous
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const p =
        inflight.get(key) ||
        (async () => {
          const result = await fetcher(controller.signal);
          cache.set(key, { data: result, ts: Date.now() });
          return result;
        })();

      inflight.set(key, p);

      const result = await p;
      setData(result);
      return result;
    } catch (e) {
      if (e?.name !== 'AbortError' && e?.name !== 'CanceledError') setError(e);
    } finally {
      inflight.delete(key);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;

    const hit = cache.get(key);
    const fresh = hit && Date.now() - hit.ts < ttlMs;

    if (fresh) {
      setData(hit.data);
      setLoading(false);
      return;
    }

    refetch();

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // key が変わったら取り直す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return { data, loading, error, refetch };
}
